import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Table,
  Select,
  Popover,
  Input,
  Modal,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  MoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Container } from "./style";
import { createVisibleToColumn } from "./VisibleToCell";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { setPromptData } from "../../../store/features/CreatePrompt";
const { confirm } = Modal;

const LIST_PROMPTS = gql`
  query ListPrompts(
    $filters: ListPromptInput
    $args: PaginationArgs
    $sortBy: SortByArgs
  ) {
    listPrompts(filters: $filters, args: $args, sortBy: $sortBy) {
      count
      limit
      offset
      total
      prompts {
        _id
        content
        date
        authorName
        isBookmarked
        completesCount
        scheduledAt
        viewsCount
        repliesCount
        uniqueVisitorsCount
        visibility
        visibilityType
        visibilityGroupId
        status
        coach {
          _id
          name
          email
          belief
          about
          experience
        }
      }
    }
  }
`;

const UPDATE_PROMPT = gql`
  mutation updatePrompt($updatePromptInput: UpdatePromptInput!) {
    updatePrompt(payload: $updatePromptInput) {
      _id
    }
  }
`;

const DELETE_PROMPT = gql`
  mutation deletePrompt($_id: String!) {
    deletePrompt(_id: $_id) {
      _id
    }
  }
`;

export default function PromptManager({ onDeleteSuccess, groups = [] }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState({});
  const [total, setTotal] = useState(0);
  const [list, setList] = useState([]);
  const initVariables = {
    filters: {
      createdBy: userInfo.coach?._id || null,
      isFromChallenge: false,
      status: null,
    },
    args: {
      limit: 10,
      offset: 0,
    },
    sortBy: {
      createdAt: -1,
      publishedAt: -1,
      scheduledAt: -1,
    },
  };

  // eslint-disable-next-line no-unused-vars
  const [getPrompts, { data, refetch }] = useLazyQuery(LIST_PROMPTS, {
    variables: initVariables,
  });
  const [updatePrompt] = useMutation(UPDATE_PROMPT);
  const [deletePrompt] = useMutation(DELETE_PROMPT);

  const statusList = [
    {
      label: "Status: All",
      value: "all",
    },
    {
      label: "Status: Published",
      value: "PUBLISHED",
    },
    {
      label: "Status: Draft",
      value: "DRAFTED",
    },
    {
      label: "Status: Scheduled",
      value: "SCHEDULED",
    },
    {
      label: "Status: Hide",
      value: "HIDDEN",
    },
  ];

  const columns = [
    {
      title: "Reflections",
      dataIndex: "content",
    },
    createVisibleToColumn({ groups, navigate }),
    {
      title: "Replies",
      dataIndex: "repliesCount",
    },
    {
      title: "Views",
      dataIndex: "viewsCount",
    },
    {
      title: "Unique visitors",
      dataIndex: "uniqueVisitorsCount",
    },
    {
      title: "Publish Date",
      dataIndex: "date",
      // sorter: true,
      render: (text) => {
        const date = new Date(text);
        const options = {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          // second: "numeric",
        };
        const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
          date
        );
        return formattedDate;
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 80,
      render: (action, row) => {
        return (
          <Popover
            placement="right"
            content={
              <ul>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onEdit(row)}
                  >
                    Edit
                  </Button>
                </li>
                <li>
                  {row.status !== "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onHide(row)}
                    >
                      Hide
                    </Button>
                  )}
                </li>
                <li>
                  {row.status === "HIDDEN" && (
                    <Button
                      type="text"
                      block
                      className="text-left"
                      onClick={() => onUnhide(row)}
                    >
                      Unhide
                    </Button>
                  )}
                </li>
                <li>
                  <Button
                    type="text"
                    block
                    className="text-left"
                    onClick={() => onDelete(row)}
                  >
                    Delete
                  </Button>
                </li>
              </ul>
            }
          >
            <Button
              type="text"
              icon={<MoreOutlined style={{ fontSize: "16px" }} />}
            ></Button>
          </Popover>
        );
      },
    },
  ];

  useEffect(() => {
    setTableParams(initVariables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams)]);

  useEffect(() => {
    if (data?.listPrompts?.prompts) {
      setList(data.listPrompts.prompts);
    } else {
      setList([]);
    }
    setTotal(data?.listPrompts?.total);
  }, [data]);

  const getData = async () => {
    if (!tableParams.args || !tableParams.args.limit) {
      return;
    }
    setLoading(true);
    // if (called) {
    await refetch(tableParams);
    // } else {
    //   await getPrompts(tableParams);
    // }
    setLoading(false);
  };
  const onSearch = (val) => {
    let param = {
      filters: Object.assign({}, tableParams.filters, {
        content: val.target.value,
      }),
      args: tableParams.args,
    };
    if (!val.target.value) {
      delete param.filters.content;
    }
    setTableParams(param);
  };
  const onStatusChange = (val) => {
    let param = { filters: tableParams.filters, args: tableParams.args };
    if (val === "all") {
      param.filters.status = null;
    } else if (val) {
      param.filters.status = val;
    }
    setTableParams(param);
  };

  const onTableChange = (pagination, filters, sorter) => {
    console.log(pagination);
    setTableParams({
      filters: tableParams.filters,
      args: {
        limit: pagination.pageSize,
        offset: pagination.pageSize * (pagination.current - 1),
      },
    });

    // `dataSource` is useless since `pageSize` changed
    if (pagination.pageSize !== tableParams.args?.limit) {
      setList([]);
    }
  };

  // Actions
  const onEdit = (data) => {
    dispatch(setPromptData(data));
    navigate("/EditPrompt");
  };
  const onHide = (data) => {
    confirm({
      title: `Hide the reflection "${data.content}"?`,
      content: "Are you sure to hide this reflection",
      okText: "Yes, hide this reflection",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        const param = {
          _id: data._id,
          status: "HIDDEN",
        };
        await updatePrompt({
          variables: {
            updatePromptInput: param,
          },
        });
        message.success(`Reflection "${data.content}" is hidden from the app!`);
        getData();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onUnhide = (data) => {
    confirm({
      title: `Unhide the reflection "${data.content}"?`,
      content: "Are you sure to unhide this reflection",
      okText: "Yes, unhide this reflection",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        const param = {
          _id: data._id,
          status: "PUBLISHED",
        };
        await updatePrompt({
          variables: {
            updatePromptInput: param,
          },
        });
        message.success(
          `Reflection "${data.content}" is unhidden adn available in the app!`
        );
        getData();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };
  const onDelete = (data) => {
    confirm({
      title: `Delete the reflection "${data.content}"?`,
      content:
        "Deleting this reflection will permanently remove it from your account. Are you sure to delete this reflection?",
      okText: "Yes, delete this reflection",
      okType: "danger",
      cancelText: "Cancel",
      async onOk() {
        await deletePrompt({
          variables: {
            _id: data._id,
          },
        });
        message.success(
          `Reflection "${data.content}" is deleted successfully!`
        );
        getData();
        onDeleteSuccess && onDeleteSuccess();
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  return (
    <Container>
      <div className="flex-between">
        <Input
          size="large"
          placeholder="Search"
          bordered={false}
          prefix={
            <SearchOutlined style={{ fontSize: "20px", marginRight: "10px" }} />
          }
          className="table-search"
          onPressEnter={onSearch}
        />
        <div className="table-filter">
          <Select
            defaultValue="Status: All"
            style={{ width: 150 }}
            onChange={onStatusChange}
            options={statusList}
          />
        </div>
      </div>
      <Table
        columns={columns}
        rowKey={(record) => record._id}
        dataSource={list}
        pagination={{ total }}
        loading={loading}
        onChange={onTableChange}
      />
    </Container>
  );
}
